import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { useFetchData } from 'services/api/useFetchData';
import { useSocketChannel } from 'services/api/updatesSocket';
import useJobInboxSpacerHeight from './functions/useJobInboxSpacerHeight';
import { generateJobInboxWeeks } from './functions/generateJobInboxWeeks';
import { generateInboxJobs } from './functions/generateInboxJobs';
import { generateWeekKey, getFirstDateOfWeek, getLastDateOfWeek, initialJobInboxDateRange, newTargetDateInCurrentOrFutureWeek } from './functions/jobInboxUtils';
import JobModal from '../jobs/JobModal';
import { formatDisplayDate } from 'internationalization/timezoneConversions';
import { formatAmount } from 'services/utils/amountFormatting';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines } from '@fortawesome/free-solid-svg-icons';
import { DragDropEvent, DraggingState } from 'services/dragdrop/DragDropContext';
import { Droppable } from 'services/dragdrop/Droppable';
import { SchedulerDraggableId, SchedulerDroppableId, SchedulingJobType } from './SchedulingTypes';
import { Draggable } from 'services/dragdrop/Draggable';
import { handleJobInboxDrag } from './functions/handleJobInboxDrag';
import { Division } from './../../views/environments/EnvironmentTypes';
import '../../style/scss/scheduling.scss'
import MultiGroupSelectButton from 'components/buttons/MultiGroupSelectButton';
import { useDeepCompareMemo } from 'use-deep-compare';

interface JobInboxProps {
    selectedDivisions: number[];
    inboxHeight: number;
    dragging: DraggingState<SchedulerDraggableId, SchedulerDroppableId> | null;
    isDragging: boolean;
    dragOver: SchedulerDroppableId | null;
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId> | null;
    onLoaded: (loaded: boolean) => void;
};

const JobInbox: React.FC<JobInboxProps> = ({ 
    selectedDivisions, inboxHeight, dragging, isDragging, dragOver, dragResult, 
    onLoaded 
}) => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, environmentSettings, userPreferences } = useSettings();
    const { initializeModal } = useModal();
    const [dateRange, setDateRange] = useState<{ from?: string, to: string}>(initialJobInboxDateRange())
    const [isInitialFetch, setIsInitialFetch] = useState(true);
    const { message: inboxJobsWebsocket } = useSocketChannel('inbox_jobs', ['inbox_jobs_updated']);
    const [visibleWeeks, setVisibleWeeks] = useState<string[]>([]);
    const [pastJobs, setPastJobs] = useState<SchedulingJobType[]>([]);
    const [untargetedJobs, setUntargetedJobs] = useState<SchedulingJobType[]>([]);
    const [weekJobs, setWeekJobs] = useState<{ [week: string]: SchedulingJobType[] }>({});
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [jobTypes, setJobTypes] = useState<{ id: number, name: string, ordering: number }[]>([]);
    const [labels, setLabels] = useState<{ id: number, name: string, color: string | null }[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<{[groupId: string]: (string | number)[]}>({ job_types: [], labels: [], conditions: [] });
    const [filtersInitialized, setFiltersInitialized] = useState(false);
    const jobInboxRef = useRef<HTMLDivElement>(null);
    const spacerHeight = useJobInboxSpacerHeight(inboxHeight, visibleWeeks, weekJobs);
    const showPurchase = activeFeatures.includes('purchases');
    const weekStartsOnSunday = false;    
    
    // Show the first weeks from now on initial render
    useEffect(() => {
        setVisibleWeeks(generateJobInboxWeeks(10));
    }, []);

    // Set the initial selected filters based on the users saved filters or default filters
    useEffect(() => {
        if (!filtersInitialized &&
            jobTypes.length > 0 &&
            // labels.length > 0 &&  // TODO: temporarily removed, otherwise job inbox is not rendered if there are no labels yet in the environment
            environmentSettings &&
            activeFeatures &&
            userPreferences
        ) {
            // Get the users saved filters
            const userSavedFilters = userPreferences.scheduling_job_inbox_filters;

            // Determine the default filters as fallback
            const defaultFilters = {
                job_types: jobTypes.map(j => j.id),
                labels: [] as number[],
                conditions: [
                    "job_created",
                    ...(environmentSettings?.scheduling_manual_to_schedule_status ? ["to_schedule"] : []),
                    ...(activeFeatures.includes("purchases") ? ["one_open_purchase", "all_purchases_delivered"] : []),
                    "incomplete"
                ]
            };

            // Use the user saved filters or fallback to the default filters
            const filtersToUse = userSavedFilters ?? defaultFilters;

            // Update the selected filters state
            setSelectedFilters({
                job_types: filtersToUse.job_types ?? [],
                labels: filtersToUse.labels ?? [],
                conditions: filtersToUse.conditions ?? []
            });

            // Set initialize filter state
            setFiltersInitialized(true);
        }
    }, [filtersInitialized, jobTypes, labels, environmentSettings, activeFeatures, userPreferences]);

    // Remove the manual to schedule status if it is turned off in the environment settings
    useEffect(() => {
        if (filtersInitialized &&
            !environmentSettings?.scheduling_manual_to_schedule_status &&
            selectedFilters.conditions.includes('to_schedule')
        ) {
            setSelectedFilters(prev => ({ ...prev, conditions: prev.conditions.filter(condition => condition !== 'to_schedule')}));

            // TODO: Eigenlijk moet ie dan ook de userPreference updaten ?.... Of misschien moet ik dat even doen via de backend
        }
    }, [environmentSettings?.scheduling_manual_to_schedule_status, filtersInitialized, selectedFilters.conditions]);

    // Determine the raw fetch params based on the states
    const fetchParamsRaw = useMemo(() => {
        if (!dateRange.to || !filtersInitialized) return null;

        return {
            from: dateRange.from,
            to: dateRange.to,
            divisions: selectedDivisions,
            job_types: (selectedFilters.job_types ?? []).map(Number),
            labels: (selectedFilters.labels ?? []).map(Number),
            status_conditions: (selectedFilters.conditions ?? []).map(String)
        };
    }, [dateRange.to, filtersInitialized, selectedDivisions, selectedFilters]);

    // Deep compare raw fetch params, because every map gets a different key even with equal data
    const fetchParams = useDeepCompareMemo(() => fetchParamsRaw, [fetchParamsRaw]);

    // Fetch the inbox jobs
    const { response: fetchedInboxJobs, loading: fetchInboxJobsLoading } = useFetchData({
        apiUrl: fetchParams?.to ? "get_job_inbox_list" : "",
        params: fetchParams
    });

    // Fetch the divisions
    const { response: fetchedDivisions } = useFetchData({ 
        apiUrl: 'get_division_list', 
        params: useMemo(() => ({ is_active: true }), []) 
    });

    // Fetch the job types
    const { response: fetchedJobTypes } = useFetchData({ 
        apiUrl: 'get_workflow_for_feature_list/job'
    });

    // Fetch the labels
    const { response: fetchedLabels } = useFetchData({ 
        apiUrl: 'get_label_for_feature_list/jobs'
    });

    // When the selected division has changed after the initial fetch, load the jobs again from the beginning
    useEffect(() => {
        if (!isInitialFetch) {
            const currentLastDate = getLastDateOfWeek(visibleWeeks);
            setDateRange({
                from: undefined,
                to: currentLastDate,
            });
        }
    }, [selectedDivisions]);

    // When the filter changes, load the jobs again from the beginning
    const handleFilterChange = (filters: {[groupId: string]: (string | number)[]}) => {
        setSelectedFilters(filters);

        // Clear the loaded jobs, the fetched jobs are merged with the existing
        // ones for the infinite scroll, so jobs that no longer match the filter
        // would otherwise stay in the list
        setPastJobs([]);
        setWeekJobs({});
        setUntargetedJobs([]);

        const currentLastDate = getLastDateOfWeek(visibleWeeks);
        setDateRange({
            from: undefined,
            to: currentLastDate,
        });
    };

    // Set the fetched inbox jobs
    useEffect(() => {
        if (fetchedInboxJobs) {
            const { updatedPastJobs, updatedUntargetedJobs, updatedWeekJobs, newWeeks } = generateInboxJobs(
                fetchedInboxJobs,
                pastJobs,
                untargetedJobs,
                weekJobs,
                selectedDivisions,
                weekStartsOnSunday
            );

            setPastJobs(updatedPastJobs);
            setWeekJobs(updatedWeekJobs);
            setUntargetedJobs(updatedUntargetedJobs);
            setVisibleWeeks(prev => Array.from(new Set([...prev, ...newWeeks])));
            setIsInitialFetch(false);
        }
    }, [fetchedInboxJobs, selectedDivisions]);

    // Set the fetched divisions
    useEffect(() => {
        if (fetchedDivisions) setDivisions(fetchedDivisions.results);
    }, [fetchedDivisions]);

    // Set the fetched job types
    useEffect(() => {
        if (fetchedJobTypes) setJobTypes(fetchedJobTypes.results);
    }, [fetchedJobTypes]);

    // Set the fetched labels
    useEffect(() => {
        if (fetchedLabels) setLabels(fetchedLabels.results);
    }, [fetchedLabels]);

    // If the data is successfully loaded on the initial fetch/render, set onLoaded to true
    useEffect(() => {
        if (isInitialFetch && fetchInboxJobsLoading === 'success') {
            onLoaded(true);
        } 
    }, [isInitialFetch, fetchInboxJobsLoading, onLoaded]);

    // Process received updates of inbox jobs by websocket
    useEffect(() => {
        const { type, message } = inboxJobsWebsocket || {};

        if (type === 'inbox_jobs_updated' ) {
            // Get the values from the message to use in this function
            const { id, target_date, status_condition, deleted, ...otherProps } = message;

            // Determine if the new target date is in the current or future weeks
            const targetDateInCurrentOrFutureWeek = newTargetDateInCurrentOrFutureWeek(target_date, weekStartsOnSunday)
            const newWeekKey = generateWeekKey(target_date, weekStartsOnSunday);

            // Update week jobs
            setWeekJobs(existingWeekJobs => {
                const updatedWeekJobs = { ...existingWeekJobs };

                // Remove the job from all existing weeks
                Object.keys(updatedWeekJobs).forEach(weekKey => {
                    updatedWeekJobs[weekKey] = updatedWeekJobs[weekKey].filter(job => job.id !== id);
                });

                // Add the job to the week that correspondents to the new target date
                if (!deleted && (status_condition === 'job_created' || status_condition === 'all_purchases_delivered' || status_condition === 'incomplete') && targetDateInCurrentOrFutureWeek) {
                    if (!updatedWeekJobs[newWeekKey]) {
                        updatedWeekJobs[newWeekKey] = [];
                    }
                    updatedWeekJobs[newWeekKey].push({ id, target_date, status_condition, ...otherProps });
                }

                return updatedWeekJobs;
            });

            // Add the job to the past jobs if the target date is in a past week
            setPastJobs(currentPastJobs => {
                let updatedPastJobs = currentPastJobs.filter(job => job.id !== id);

                if (!deleted && (status_condition === 'job_created' || status_condition === 'all_purchases_delivered' || status_condition === 'incomplete') && !targetDateInCurrentOrFutureWeek) {
                    updatedPastJobs.push({ id, target_date, status_condition, ...otherProps });
                }

                return updatedPastJobs;
            });
        }
    }, [inboxJobsWebsocket]);

    // Load more week rows when scrolling down
    useEffect(() => {
        const jobInboxElement = jobInboxRef.current;
        if (jobInboxElement == null) return;

        // Execute the loading when scrolling
        const handleScroll = () => {
            // Skip when dragging an inbox job, otherwise users may drag into new weeks which doesn't exist as destination yet
            if (isDragging) return;

            if (jobInboxElement && jobInboxElement.scrollTop + jobInboxElement.clientHeight >= jobInboxElement.scrollHeight - 10) {
                loadAdditionalInboxRows();
            };
        };
    
        // Add and remove event listener for scroll event
        jobInboxElement.addEventListener('scroll', handleScroll);
        return () => jobInboxElement.removeEventListener('scroll', handleScroll);
    }, [visibleWeeks, isDragging]);

    // Create and load additional weeks into the job inbox
    const loadAdditionalInboxRows = () => {
        // Determine the current last visible week
        const currentLastWeek = visibleWeeks[visibleWeeks.length - 1];

        // Generate the desired weeks from the current last week
        const additionalWeeksToCreate = 5
        const generatedAdditionalWeeks = generateJobInboxWeeks(additionalWeeksToCreate, currentLastWeek);

        // Add the generated additional weeks to the visible weeks 
        setVisibleWeeks(prev => Array.from(new Set([...prev, ...generatedAdditionalWeeks])));

        // Get the first and last date of the generated weeks
        const firstDate = getFirstDateOfWeek(generatedAdditionalWeeks);
        const lastDate = getLastDateOfWeek(generatedAdditionalWeeks);

        // Update the date range state to trigger a new fetch
        setDateRange(prev => ({ from: prev.from ?? firstDate, to: lastDate }));
    };

    // Calculates the total estimated duration of all jobs within an inbox row
    const calculateRowDuration = (jobs: SchedulingJobType[]): string | null => {
        if (!jobs || jobs.length === 0) {
            return null; 
        }

        const totalDuration = jobs.reduce((total, job) => total + (Number(job.estimated_duration) || 0), 0);
        return formatAmount(totalDuration, userLocale);
    };

    // Generate the week label to show in the week row
    const generateWeekLabel = (weekKey: string, previousWeekKey?: string, isCurrentWeek: boolean = false): string => {
        const [year, week] = weekKey.split('-').map(Number);

        // Determine if the week is the first week of the year
        const firstWeekOfYear = week === 1 || ((previousWeekKey && year !== parseInt(previousWeekKey.split('-')[0])));

        // In case of the first week of the year, show the year
        let weekLabel;
        if (isCurrentWeek) {
            weekLabel = `${t('date_time.general.current_week')}`;
        } else if (firstWeekOfYear) {
            weekLabel = `${t('date_time.general.week_short')} ${week} (${year})`;
        } else {
            weekLabel = `${t('date_time.general.week_short')} ${week}`;
        }

        return weekLabel;
    };

    // Get the division name
    const getDivisionName = (divisionId?: number) => {
        if (!divisionId) return null;
  
        const division = divisions.find(division => division.id === divisionId);
        return division?.name ?? null;
    };
    
    // Handle the completion of dragging of an inbox job
    useEffect(() => {
        if (dragResult) {
            const handleInboxJobDrag = async () => {
                handleJobInboxDrag(
                    dragResult,
                    pastJobs,
                    untargetedJobs,
                    weekJobs,
                    weekStartsOnSunday,
                    userLocale,
                    setFloatingAlert,
                    setPastJobs,
                    setUntargetedJobs,
                    setWeekJobs
                );                
            }
            handleInboxJobDrag();
        }
    }, [dragResult]);

    // Render a dragging placeholder, when the drag is over the current droppable area
    const draggingPlaceholder = (droppableId: SchedulerDroppableId) => {
        if (dragging && (JSON.stringify(droppableId) === JSON.stringify(dragOver))) {
            return (
                <div
                    style={{
                        height: dragging.origin.rect?.height || '50px',
                        width: '100%',
                        backgroundColor: '#D8D8D8BF',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                    }}
                />
            );
        }
        return null;
    };

    // Status conditions shown in the inbox, delivered purchases join to schedule
    const allowedStatusConditions = [
        'job_created',
        'incomplete',
        ...(environmentSettings?.scheduling_manual_to_schedule_status ? ['to_schedule'] : []),
        ...(activeFeatures.includes("purchases") ? ['one_open_purchase', 'all_purchases_delivered'] : []),
        ...(selectedFilters.conditions.includes('to_schedule') ? ['all_purchases_delivered'] : [])
    ];

    // Render an inbox row
    const renderInboxRow = ({ jobs, section, weekKey, label, calculateDuration }: {
        jobs: SchedulingJobType[], 
        section: 'pastJobs' | 'untargetedJobs' | 'weekJobs', 
        weekKey?: string, 
        label: string, 
        calculateDuration: (jobs: SchedulingJobType[]) => string | null 
    }) => (
        <Droppable droppableId={{ type: 'jobInbox', section, ...weekKey ? { weekKey } : {} }} 
                   showPlaceholder={false}
                   key={`${section}${weekKey ? `-${weekKey}` : ``}`}>
            <div className='inbox-row'>
                <div className='header'>
                    <span className='label'>
                        {t(label)}
                    </span>
                    <span className='budget'>
                        {calculateDuration(jobs) ? `${calculateDuration(jobs)} ${t('date_time.general.hour.singular')}` : ''}
                    </span>
                </div>
                <div>
                    {jobs.filter((job => !job.deleted && job.status_condition && (
                        !job.deleted && allowedStatusConditions.includes(job.status_condition)
                    ))).map((job) => (
                        renderJobInboxItem(job, section, weekKey)
                    ))}
                    {draggingPlaceholder({ type: 'jobInbox', section, ...weekKey ? { weekKey } : {} })}
                    <div className='row-space'></div>
                </div>
            </div>
        </Droppable>
    );

    // Renders a job inbox item
    const renderJobInboxItem = (
        job: SchedulingJobType, 
        section: 'pastJobs' | 'untargetedJobs' | 'weekJobs',
        weekKey?: string
    ) => (
        <Draggable key={job.id}
                   draggableId={{ type: 'jobInbox', job, ...weekKey ? { weekKey } : {} }}
                   sourceDroppableArea={{ type: 'jobInbox', section, ...weekKey ? { weekKey } : {} }}>
            <div className='job-inbox-item'
                 onClick={() => initializeModal(React.cloneElement(<JobModal />, { itemId: job.id }), { itemId: job.id, modalSize: 'large' }) }>
                <div className='info'>
                    <div className='badges'>
                        <div className="label-badge-container">
                            {job.labels?.map((label, index) => (
                                <div key={index} className={`label-badge ${label.color}`}>
                                    {label.name}
                                </div>
                            ))}
                            <span className='remark'>
                                {job.target_date && `${formatDisplayDate(new Date(job.target_date), userLocale)}`}
                                {job.target_date && job.estimated_duration && (` • `)}
                                {job.estimated_duration && `${formatAmount(Number(job.estimated_duration), userLocale)} ${t('date_time.general.hour.singular')}`}
                            </span>
                        </div>
                        <div>
                            {environmentSettings?.scheduling_manual_to_schedule_status && (
                                (job.status_condition === 'job_created' || job.status_condition === 'to_schedule') && (
                                    <span className={`status-badge ${job.status_condition}`}>{t(`job.status_condition.${job.status_condition}`)}</span>
                                )
                            )}
                            {job.status_condition === 'one_open_purchase' && job.expected_delivery_date && (
                                <span className="purchase-date">
                                    {formatDisplayDate(new Date(job.expected_delivery_date), userLocale)}
                                </span>
                            )}
                            {(job.status_condition === 'one_open_purchase' ||
                              job.status_condition === 'all_purchases_delivered' ||
                              job.status_condition === 'incomplete') && (
                                <span className={`status-badge ${job.status_condition}`}>{t(`job.status_condition.${job.status_condition}`)}</span>
                            )}
                        </div>
                    </div>
                    
                    <div className='title'>
                        {job.type_name}
                        {job.number && <span> {job.number}</span>}
                        {job.client_name && (
                            <span> - {job.client_name.slice(0, 20)}</span>
                        )}
                        {job.work_location_city && <span> - {job.work_location_city}</span>}
                        {activeFeatures.includes('divisions') && job.division_id && (
                            (selectedDivisions.length === 0 || selectedDivisions.length > 1) && (
                                <span> - {getDivisionName(job.division_id)}</span>
                            )
                        )}
                    </div>

                    {job.main_description && (
                        <div className="remark">{job.main_description}</div>
                    )}

                    
                </div>
                <div className='order-icon'>
                    <FontAwesomeIcon icon={faGripLines} />
                </div>
            </div>
        </Draggable>
    );

    // Set the job inbox filters
    const jobInboxFilters = useMemo(() => {
        return [
            {
                id: "job_types",
                label: "scheduling.job_inbox.job_types_filter_label",
                options: jobTypes.map(j => ({ value: j.id, label: j.name }))
            },
            ...(labels.length > 0 ? [{
                id: "labels",
                label: "scheduling.job_inbox.labels_filter_label",
                optionType: "label-badge" as const,
                options: labels.map(l => ({
                    value: l.id,
                    label: l.name,
                    color: l.color
                }))
            }] : []),
            {
                id: "conditions",
                label: "scheduling.job_inbox.status_filter_label",
                optionType: "status-badge" as const,
                options: [
                    { value: "job_created", label: t("job.status_condition.job_created"), color: "job_created" },
                    ...(showPurchase ? [
                        { value: "one_open_purchase", label: t("job.status_condition.one_open_purchase"), color: "one_open_purchase" },
                        { value: "all_purchases_delivered", label: t("job.status_condition.all_purchases_delivered"), color: "all_purchases_delivered" },
                    ] : []),
                    ...(environmentSettings.scheduling_manual_to_schedule_status ? [{
                        value: "to_schedule", label: t("job.status_condition.to_schedule"), color: "to_schedule"
                    }] : []),
                    { value: "incomplete", label: t("job.status_condition.incomplete"), color: "incomplete" },
                ]
            }
        ];
    }, [jobTypes, labels, environmentSettings, activeFeatures, t]);

    return (
        <div className='job-inbox'>
            <div className='header'>
                <div className='title'>
                    {t('scheduling.job_inbox.job_inbox_title')}
                    {/* <div className='help tooltip-icon'>
                        <FontAwesomeIcon icon={faCircleQuestion} />
                        <span className="tooltip tooltip-bottom">{t('scheduling.job_inbox.job_inbox_tooltip')}</span>
                    </div> */}
                </div>
                <div className='controls'>
                    {/* <input
                        name="jobinbox_search"
                        type='text'
                        placeholder={t('forms.search_placeholder')}
                        // value={searchTerm}
                        // onChange={handleSearchChange}
                    /> */}
                    <MultiGroupSelectButton
                        label="scheduling.job_inbox.filter_button_label"
                        size="extra-small"
                        groups={jobInboxFilters}
                        selected={selectedFilters}
                        onChange={handleFilterChange}
                    />
                </div>
            </div>
            <div className='inbox' 
                 style={{ height: '100%', maxHeight: inboxHeight }} 
                 ref={jobInboxRef}>
                {isInitialFetch ? (
                    fetchInboxJobsLoading === 'show-loader' && (
                        <div className='inbox-loader'>
                            <div className="loader"></div>
                        </div>
                    )
                ) : (
                    <>
                        <div className='content'>
                            <div className='inbox-rows'>
                                {pastJobs.length > 0 && (
                                    renderInboxRow({
                                        jobs: pastJobs,
                                        section: 'pastJobs',
                                        label: 'scheduling.job_inbox.past_jobs_label',
                                        calculateDuration: calculateRowDuration
                                    })
                                )}
                                {untargetedJobs.length > 0 && (
                                    renderInboxRow({
                                        jobs: untargetedJobs,
                                        section: 'untargetedJobs',
                                        label: 'scheduling.job_inbox.untargeted_jobs_label',
                                        calculateDuration: calculateRowDuration
                                    })
                                )}
                                {visibleWeeks.map((weekKey, index) => (
                                    renderInboxRow({
                                        jobs: weekJobs[weekKey] || [],
                                        section: 'weekJobs',
                                        weekKey,
                                        label: generateWeekLabel(weekKey, index > 0 ? visibleWeeks[index - 1] : undefined, index === 0),
                                        calculateDuration: calculateRowDuration
                                    })
                                ))}
                            </div>
                            <div style={{ height: spacerHeight }}></div>
                        </div>   
                        <span className='scroll-text'>
                            <Trans i18nKey="scheduling.job_inbox.load_more_weeks_helpertext" 
                                components={[<span onClick={loadAdditionalInboxRows} className="scroll-text-button"/>]}>
                                {t('scheduling.job_inbox.load_more_weeks_helpertext')}
                            </Trans>
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

export default JobInbox;